import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.warn('Scroll behavior not supported:', error);
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const handleImageError = () => {
    setImageError(true);
    console.warn('Logo image failed to load');
  };

  const handleMenuToggle = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Menu button clicked!'); // Debug log
    setIsMobileMenuOpen(prev => !prev);
  };

  const handleMenuClose = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between relative">
            {/* Logo */}
            <Link to="/" className="cursor-pointer relative z-[5] pointer-events-auto" aria-label="Home">
              {!imageError ? (
                <img
                  src="https://static.readdy.ai/image/6b8c7e9e23efe7296b6664942db70801/627259e4050d281bad388fdf5631228c.png"
                  alt="Minuu Property Services"
                  className="h-24 object-contain object-top pointer-events-none"
                  onError={handleImageError}
                />
              ) : (
                <div className="h-16 w-auto flex items-center justify-center text-black font-bold text-xl">
                  Minuu Property Services
                </div>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-black hover:text-gray-600 transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTA Button */}
            <Link
              to="/contact#quote-form"
              className="hidden md:flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Request a Quote
              <i className="ri-arrow-right-line"></i>
            </Link>

            {/* Mobile Menu Button - HIGHEST Z-INDEX */}
            <button
              type="button"
              className="md:hidden fixed top-6 right-4 z-[200] w-10 h-10 flex items-center justify-center cursor-pointer bg-transparent touch-manipulation active:scale-95 transition-transform"
              onTouchEnd={handleMenuToggle}
              onClick={handleMenuToggle}
              aria-label="Toggle menu"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-black pointer-events-none`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[98] md:hidden backdrop-blur-sm"
          onTouchEnd={handleMenuClose}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-white z-[99] transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-6 py-8 border-b border-gray-100">
            <span className="text-black font-bold text-xl tracking-tight">Menu</span>
          </div>

          {/* Mobile Navigation Links */}
          <div className="flex flex-col px-6 py-8 gap-1 flex-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-black hover:bg-gray-50 transition-all duration-200 font-medium py-4 px-4 cursor-pointer rounded-lg text-base"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile CTA Button - Bottom */}
          <div className="px-6 py-6 border-t border-gray-100">
            <Link
              to="/contact#quote-form"
              className="flex items-center justify-center gap-2 bg-black text-white px-6 py-4 rounded-lg font-medium hover:bg-gray-800 transition-all duration-200 whitespace-nowrap cursor-pointer w-full"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Request a Quote
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
